

import React, { useState } from 'react';
import { useDailyActivities } from '@/features/projects/hooks/useDailyActivities';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Button } from "@/shared/ui/button";
import { Skeleton } from "@/shared/ui/skeleton";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/shared/ui/pagination";
import { Alert, AlertDescription, AlertTitle } from "@/shared/ui/alert";
import { Terminal, CalendarDays, Users, Car, TruckIcon, UserCircle, StickyNote, Users2, Truck, User } from "lucide-react";
import type { DailyActivityWithAssignments } from '@/features/projects/types';
import { Badge } from "@/shared/ui/badge";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/shared/ui/accordion";
import { Label } from "@/shared/ui/label";

/**
 * 日次活動タブのプロパティ
 * @param projectId プロジェクトID
 * @param onDataUpdated データ更新時のコールバック
 */
interface DailyActivitiesTabProps {
    projectId: string;
    onDataUpdated?: () => void; // 親コンポーネントへの更新通知
}

/**
 * 日付フォーマット
 * @param dateString 日付文字列
 * @returns 日付フォーマット
 */
const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' });
    } catch {
        return '-';
    }
};

/**
 * 今日の日付
 */
const today = new Date();
const oneMonthAgo = new Date();
oneMonthAgo.setMonth(today.getMonth() - 1);
const initialEndDate = today.toISOString().split('T')[0];
const initialStartDate = oneMonthAgo.toISOString().split('T')[0];

function ActivityCardSkeleton() {
    return (
        <Card className="mb-4">
            <CardHeader>
                <Skeleton className="h-6 w-1/4 mb-2" />
                <Skeleton className="h-4 w-1/2" />
            </CardHeader>
            <CardContent className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-full" />
            </CardContent>
        </Card>
    );
}

/**
 * 日次活動タブ
 * @param projectId プロジェクトID
 * @param onDataUpdated データ更新時のコールバック
 * @returns 日次活動タブ
 */
export default function DailyActivitiesTab({ projectId, onDataUpdated }: DailyActivitiesTabProps) {
    const [startDate, setStartDate] = useState(initialStartDate);
    const [endDate, setEndDate] = useState(initialEndDate);

    const { activities, totalCount, isLoading, error, setFilters, pagination, setPage } =
        useDailyActivities(projectId, startDate, endDate);

    const handleFilterApply = () => {
        setFilters({ start_date: startDate, end_date: endDate });
        if (onDataUpdated) {
            onDataUpdated();
        }
    };

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center"><CalendarDays className="mr-2 h-5 w-5 text-primary" />表示期間の設定</CardTitle>
                    <CardDescription>表示する日次活動の期間を指定してください。</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col sm:flex-row sm:flex-wrap gap-4 items-end">
                    <div className="flex-grow min-w-[200px]">
                        <Label htmlFor="start-date" className="mb-1.5 block">開始日</Label>
                        <Input
                            id="start-date"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full"
                        />
                    </div>
                    <div className="flex-grow min-w-[200px]">
                        <Label htmlFor="end-date" className="mb-1.5 block">終了日</Label>
                        <Input
                            id="end-date"
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full"
                        />
                    </div>
                    <Button onClick={handleFilterApply} disabled={isLoading} className="w-full sm:w-auto sm:mt-auto mt-2">
                        <CalendarDays className="mr-2 h-4 w-4" />
                        期間を適用
                    </Button>
                </CardContent>
            </Card>

            {isLoading && (
                <div className="space-y-4">
                    {[...Array(3)].map((_, i) => <ActivityCardSkeleton key={i} />)}
                </div>
            )}

            {error && (
                <Alert variant="destructive">
                    <Terminal className="h-4 w-4" />
                    <AlertTitle>エラー</AlertTitle>
                    <AlertDescription>
                        日次活動データの読み込みに失敗しました。ネットワーク接続を確認するか、しばらくしてからもう一度お試しください。(エラー: {error?.message || '不明なエラー'})
                    </AlertDescription>
                </Alert>
            )}

            {!isLoading && !error && (
                <>
                    {activities && activities.length > 0 ? (
                        <div className="space-y-3">
                            <p className="text-sm text-muted-foreground pb-1">全 {totalCount ?? 0} 件の日次活動が見つかりました（期間: {formatDate(startDate)} 〜 {formatDate(endDate)}）。</p>
                            <Accordion type="single" collapsible className="w-full">
                                {activities.map((activity: DailyActivityWithAssignments) => (
                                    <AccordionItem value={activity.id} key={activity.id}>
                                        <AccordionTrigger className="hover:bg-muted/50 px-4 rounded-md data-[state=open]:bg-muted/60 data-[state=open]:shadow-sm">
                                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full text-left">
                                                <div className="flex items-center mb-1 sm:mb-0">
                                                    <CalendarDays className="mr-2 h-5 w-5 text-primary flex-shrink-0" />
                                                    <span className="font-medium">{formatDate((activity as any).schedule_date || (activity as any).activity_date)}</span>
                                                    {activity.project_zone?.zone_name && (
                                                        <Badge variant="outline" className="ml-2 whitespace-nowrap">{activity.project_zone.zone_name}</Badge>
                                                    )}
                                                </div>
                                                <div className="flex items-center space-x-3 text-xs text-muted-foreground mt-1 sm:mt-0 sm:ml-4">
                                                    {activity.craftsman_assignments && activity.craftsman_assignments.length > 0 && (
                                                        <span className="flex items-center">
                                                            <Users2 className="mr-1 h-3.5 w-3.5" /> {activity.craftsman_assignments.length}人
                                                        </span>
                                                    )}
                                                    {activity.truck_assignments && activity.truck_assignments.length > 0 && (
                                                        <span className="flex items-center">
                                                            <TruckIcon className="mr-1 h-3.5 w-3.5" /> {activity.truck_assignments.length}台
                                                        </span>
                                                    )}
                                                    {activity.notes && (
                                                        <span className="flex items-center">
                                                            <StickyNote className="mr-1 h-3.5 w-3.5" /> メモ有
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="pt-0 pb-4 px-4 bg-background">
                                            <div className="border-t mt-2 pt-4 space-y-5">
                                                {activity.craftsman_assignments && activity.craftsman_assignments.length > 0 && (
                                                    <div>
                                                        <h4 className="text-sm font-semibold mb-2.5 flex items-center"><Users className="mr-2 h-4 w-4 text-muted-foreground" />割り当て職人</h4>
                                                        <ul className="pl-5 text-sm space-y-2">
                                                            {activity.craftsman_assignments.map(ca => (
                                                                <li key={ca.id} className="text-muted-foreground flex items-center">
                                                                    <UserCircle className="mr-2 h-4 w-4 flex-shrink-0 text-gray-400" />
                                                                    <span>{ca.craftsman_name || ca.craftsman?.name || '不明な職人'}</span>
                                                                    {(ca.company_vehicle_name || ca.company_vehicle?.name || ca.vehicle_id) && (
                                                                        <Badge variant="outline" className="ml-2 py-0.5 px-1.5 text-xs">
                                                                            <Car className="inline mr-1 h-3 w-3" />
                                                                            {ca.company_vehicle_name || ca.company_vehicle?.name || (ca.vehicle_id ? '社用車あり' : '')}
                                                                        </Badge>
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                {activity.truck_assignments && activity.truck_assignments.length > 0 && (
                                                    <div>
                                                        <h4 className="text-sm font-semibold mb-2.5 flex items-center"><TruckIcon className="mr-2 h-4 w-4 text-muted-foreground" />割り当てトラック</h4>
                                                        <ul className="pl-5 text-sm space-y-2">
                                                            {activity.truck_assignments.map(ta => (
                                                                <li key={ta.id} className="text-muted-foreground flex items-center">
                                                                    <Truck className="mr-1 h-4 w-4 text-orange-500" />
                                                                    <span>{(ta as any).license_plate || (ta.truck as any)?.name || '不明なトラック'}</span>
                                                                    {(((ta as any).truck_driver_name) || (ta.truck_driver as any)?.name || ta.driver_id) && (
                                                                        <Badge variant="outline" className="ml-2 py-0.5 px-1.5 text-xs">
                                                                            <User className="inline mr-1 h-3 w-3" />
                                                                            {((ta as any).truck_driver_name) || (ta.truck_driver as any)?.name || (ta.driver_id ? '不明な運転手' : '')}
                                                                        </Badge>
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                {activity.notes && (
                                                    <div>
                                                        <h4 className="text-sm font-semibold mb-2.5 flex items-center"><StickyNote className="mr-2 h-4 w-4 text-muted-foreground" />活動メモ</h4>
                                                        <p className="text-sm text-foreground bg-muted/50 p-3 rounded-md whitespace-pre-wrap leading-relaxed border">{activity.notes}</p>
                                                    </div>
                                                )}
                                                {(!activity.craftsman_assignments || activity.craftsman_assignments.length === 0) &&
                                                    (!activity.truck_assignments || activity.truck_assignments.length === 0) &&
                                                    !activity.notes && (
                                                        <p className="text-sm text-muted-foreground py-4 text-center">この日の活動詳細情報はありません。</p>
                                                    )}
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        </div>
                    ) : (
                        <Card>
                            <CardContent className="pt-6 pb-6 text-center">
                                <CalendarDays className="mx-auto h-12 w-12 text-muted-foreground opacity-50 mb-3" />
                                <p className="font-medium text-muted-foreground">指定された期間の日次活動はありません。</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    期間: {formatDate(startDate)} 〜 {formatDate(endDate)}
                                </p>
                            </CardContent>
                        </Card>
                    )}

                    {activities && activities.length > 0 && pagination.totalPages > 1 && (
                        <div className="mt-6 flex justify-center">
                            <Pagination>
                                <PaginationContent>
                                    <PaginationItem>
                                        <PaginationPrevious
                                            href="#"
                                            onClick={(e) => { e.preventDefault(); setPage(pagination.currentPage - 1); }}
                                            aria-disabled={pagination.currentPage <= 1}
                                            tabIndex={pagination.currentPage <= 1 ? -1 : undefined}
                                            className={pagination.currentPage <= 1 ? "pointer-events-none opacity-50" : undefined}
                                        />
                                    </PaginationItem>
                                    {[...Array(pagination.totalPages)].map((_, i) => (
                                        <PaginationItem key={i + 1}>
                                            <PaginationLink
                                                href="#"
                                                onClick={(e) => { e.preventDefault(); setPage(i + 1); }}
                                                isActive={pagination.currentPage === i + 1}
                                            >
                                                {i + 1}
                                            </PaginationLink>
                                        </PaginationItem>
                                    ))}
                                    <PaginationItem>
                                        <PaginationNext
                                            href="#"
                                            onClick={(e) => { e.preventDefault(); setPage(pagination.currentPage + 1); }}
                                            aria-disabled={pagination.currentPage >= pagination.totalPages}
                                            tabIndex={pagination.currentPage >= pagination.totalPages ? -1 : undefined}
                                            className={pagination.currentPage >= pagination.totalPages ? "pointer-events-none opacity-50" : undefined}
                                        />
                                    </PaginationItem>
                                </PaginationContent>
                            </Pagination>
                        </div>
                    )}
                </>
            )}
        </div>
    );
} 